import { ProviderEnum } from '@/types/common';
import {
  isNuveiSuccessResponse,
  isNuveiFailedResponse,
  isNuveiPendingResponse,
  isNuveiRedirectResponse,
  isBrainTreeSuccessResponse,
  isBrainTreeFailedResponse,
  isBrainTreeRedirectResponse,
  isPaypalSuccessResponse,
  isPaypalFailedResponse,
  isAdyenFinalizePaymentResponse,
  isAdyenRedirectResponse,
  isAdyenFailedResponse,
  isAdyenSuccessResponse,
} from '@/types/guards';

/**
 * Decides whether a payment result will be left UNHANDLED by its provider
 * handler in {@link usePaymentResults} — i.e. the response shape matched none
 * of that provider's success / failure / redirect / 3DS guards, so the handler
 * takes no terminal action.
 *
 * ── Why this exists ──────────────────────────────────────────────────────
 * Every provider handler acts only when the raw response matches a known
 * shape. A malformed / empty / `null` response (for example a handler upstream
 * that swallowed an error and resolved `null`) matches nothing, so the handler
 * returns without releasing `isInPaymentProcess`. The shopper is then frozen on
 * the ProcessingLoader with no recovery short of a full refresh — money may or
 * may not have moved, and they cannot retry. The orchestrator uses this
 * predicate as a generic safety net: when it returns `true`, release the loader
 * and surface a recoverable error.
 *
 * ── Design constraints ───────────────────────────────────────────────────
 * - PURE & read-only: this is only a composition of the EXISTING type guards.
 *   It owns no provider side-effects and intentionally duplicates none of the
 *   handlers' branching/behavior — it merely answers "would any handler branch
 *   have matched this response?". The provider handling logic is untouched.
 * - Single source of truth: the orchestrator never inspects provider-specific
 *   response shapes itself; it delegates that decision entirely to this helper.
 *
 * ── Per-provider notes ───────────────────────────────────────────────────
 * - STRIPE: returns `false` (always treated as handled). The Stripe handler
 *   already has its own internal catch-all that releases the loader for
 *   unrecognized responses, so the orchestrator safety net must NOT also fire
 *   for it — otherwise `onError` would be invoked twice.
 * - ADYEN: a result is handled only when it is a finalize-payment response AND
 *   resolves to redirect / failed / success. A finalize response with an
 *   unknown status, or a non-finalize response, is unhandled.
 * - default (provider could not be resolved, e.g. a misconfigured / unknown
 *   payment method): there is no handler at all, so the result is unhandled.
 */
export const isUnhandledPaymentResult = (results: unknown, provider?: ProviderEnum): boolean => {
  switch (provider) {
    case ProviderEnum.STRIPE:
      return false;
    case ProviderEnum.NUVEI:
      return !(
        isNuveiSuccessResponse(results, provider) ||
        isNuveiFailedResponse(results, provider) ||
        isNuveiPendingResponse(results, provider) ||
        isNuveiRedirectResponse(results, provider)
      );
    case ProviderEnum.BRAINTREE:
      return !(
        isBrainTreeSuccessResponse(results, provider) ||
        isBrainTreeFailedResponse(results, provider) ||
        isBrainTreeRedirectResponse(results, provider)
      );
    case ProviderEnum.PAYPAL:
      return !(isPaypalSuccessResponse(results, provider) || isPaypalFailedResponse(results, provider));
    case ProviderEnum.ADYEN:
      return !(
        isAdyenFinalizePaymentResponse(results) &&
        (isAdyenRedirectResponse(results) || isAdyenFailedResponse(results) || isAdyenSuccessResponse(results))
      );
    default:
      return true;
  }
};
